import React, { useRef, useState, useEffect } from 'react';
import { motion, useScroll, useTransform, useSpring, useMotionValue } from 'framer-motion';
import { Sparkles, ArrowRight, Star } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import useTextScramble from '../hooks/useTextScramble';
import WaveBackground from './ui/WaveBackground';
import '../styles/design-system.css';
import '../styles/scroll-animations.css';
import heroPhoto from '../assets/about.png';

/* ── Floating gold particles ───────────────────────────── */
const particles = [
  { size: 6,  x: '12%', y: '20%', dur: 5.2, delay: 0   },
  { size: 4,  x: '75%', y: '15%', dur: 4.0, delay: 0.8 },
  { size: 8,  x: '85%', y: '55%', dur: 6.1, delay: 1.4 },
  { size: 5,  x: '8%',  y: '65%', dur: 4.8, delay: 0.3 },
  { size: 3,  x: '60%', y: '80%', dur: 3.9, delay: 1.1 },
  { size: 7,  x: '45%', y: '10%', dur: 5.5, delay: 0.6 },
  { size: 4,  x: '30%', y: '90%', dur: 4.3, delay: 1.7 },
  { size: 5,  x: '90%', y: '35%', dur: 5.8, delay: 0.2 },
];

const HeroSection = () => {
  const { heroData, contactData, language, theme } = useLanguage();
  const sectionRef = useRef(null);

  /* ── Text scramble on mount ─── */
  const [scrambleReady, setScrambleReady] = useState(false);
  useEffect(() => { const t = setTimeout(() => setScrambleReady(true), 800); return () => clearTimeout(t); }, []);
  const scrambledTitle = useTextScramble(heroData?.title || 'Lucimar Bizoni', scrambleReady, 1400);

  /* ── Mouse parallax for particles ─── */
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const pX = useSpring(mouseX, { stiffness: 40, damping: 20 });
  const pY = useSpring(mouseY, { stiffness: 40, damping: 20 });

  const handleMouseMove = (e) => {
    const { innerWidth: w, innerHeight: h } = window;
    mouseX.set((e.clientX / w - 0.5) * 30);
    mouseY.set((e.clientY / h - 0.5) * 20);
  };

  /* ── Scroll-linked parallax ─── */
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start'],
  });

  // Photo moves up slower than the scroll (parallax)
  const rawPhotoY   = useTransform(scrollYProgress, [0, 1], ['0%', '25%']);
  const rawContentY = useTransform(scrollYProgress, [0, 1], ['0%', '-8%']);
  const rawOpacity  = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const rawScale    = useTransform(scrollYProgress, [0, 1], [1, 1.06]);

  const photoY    = useSpring(rawPhotoY,   { stiffness: 60, damping: 20 });
  const contentY  = useSpring(rawContentY, { stiffness: 60, damping: 20 });
  const opacity   = useSpring(rawOpacity,  { stiffness: 80, damping: 25 });
  const photoScale = useSpring(rawScale,   { stiffness: 60, damping: 20 });

  const handleNavClick = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const bgFrom = theme === 'dark' ? '#121212' : '#FAF7F2';
  const bgGrad = theme === 'dark'
    ? 'linear-gradient(to right, transparent, #121212)'
    : 'linear-gradient(to right, transparent, #FAF7F2)';
  const bgFadeBot = theme === 'dark'
    ? 'linear-gradient(to top, #121212, transparent)'
    : 'linear-gradient(to top, #FAF7F2, transparent)';

  return (
    <section id="hero" ref={sectionRef} className="hero-section grain-overlay" style={{ position: 'relative' }} onMouseMove={handleMouseMove}>

      {/* ── Wave Background (z=1, behind everything) ─── */}
      <WaveBackground
        strokeColor={theme === 'dark' ? '#E8C96A' : '#B8860B'}
        opacity={theme === 'dark' ? 0.38 : 0.1}
        pointerSize={0}
      />

      {/* ── Floating Particles (react to mouse) ─── */}
      <motion.div style={{ x: pX, y: pY, position: 'absolute', inset: 0, zIndex: 3, pointerEvents: 'none' }}>
        {particles.map((p, i) => (
          <div
            key={i}
            className="particle"
            style={{
              width: p.size,
              height: p.size,
              left: p.x,
              top: p.y,
              '--duration': `${p.dur}s`,
              '--delay': `${p.delay}s`,
              opacity: 0.5,
              position: 'absolute',
            }}
          />
        ))}
      </motion.div>

      {/* ── Background Photo — LEFT (parallax) ─── */}
      <div
        className="hidden lg:block"
        style={{ position: 'absolute', top: '70px', left: 0, bottom: 0, width: '50%', zIndex: 5, overflow: 'hidden' }}
      >
        {/* Right edge fade */}
        <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: '35%', background: bgGrad, zIndex: 2 }} />
        {/* Bottom fade */}
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '80px', background: bgFadeBot, zIndex: 2 }} />

        <motion.div style={{ y: photoY, scale: photoScale, width: '100%', height: '100%' }}>
          <img
            src={heroPhoto}
            alt="Lucimar Bizoni"
            fetchpriority="high"
            decoding="async"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center 15%',
              zIndex: 1,
              WebkitMaskImage: 'radial-gradient(ellipse 90% 95% at 40% 50%, black 60%, transparent 100%)',
              maskImage: 'radial-gradient(ellipse 90% 95% at 40% 50%, black 60%, transparent 100%)',
              filter: theme === 'dark' ? 'brightness(0.92) contrast(1.05)' : 'none',
            }}
          />
        </motion.div>
      </div>

      {/* ── Content — RIGHT (scroll fade) ─── */}
      <motion.div className="container relative z-20" style={{ y: contentY, opacity }}>
        <div className="grid lg:grid-cols-2 gap-8 items-center min-h-screen py-24 lg:pt-32 lg:pb-24">
          {/* Empty space for photo */}
          <div className="hidden lg:block" />

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
            className="text-left lg:pl-8"
          >
            {/* Announcement Badge */}
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.94 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
              style={{ background: 'var(--accent-gold-200)', border: '1px solid var(--accent-gold-400)' }}
            >
              <Sparkles size={14} style={{ color: 'var(--accent-gold-400)' }} />
              <span className="mono-text" style={{ color: 'var(--text-secondary)' }}>
                {heroData.announcement}
              </span>
            </motion.div>

            {/* Hero Title — scramble reveal */}
            <div className="overflow-hidden mb-4">
              <motion.h1
                className="heading-hero"
                initial={{ y: '110%' }}
                animate={{ y: 0 }}
                transition={{ duration: 0.85, delay: 0.65, ease: [0.16, 1, 0.3, 1] }}
              >
                <span
                  className="gold-shimmer"
                  translate="no"
                  style={{ fontFamily: scrambleReady ? undefined : 'monospace', letterSpacing: scrambleReady ? undefined : '0.02em' }}
                >
                  {scrambledTitle}
                </span>
              </motion.h1>
            </div>

            {/* Subtitle */}
            <div className="overflow-hidden mb-4">
              <motion.p
                className="body-large"
                style={{ color: 'var(--text-secondary)' }}
                initial={{ y: '110%' }}
                animate={{ y: 0 }}
                transition={{ duration: 0.75, delay: 0.75, ease: [0.16, 1, 0.3, 1] }}
              >
                {heroData.subtitle}
              </motion.p>
            </div>

            {/* Description */}
            <motion.p
              className="body-medium mb-8"
              style={{ color: 'var(--text-muted)', maxWidth: '480px' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.9 }}
            >
              {heroData.description}
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.0, ease: [0.16, 1, 0.3, 1] }}
              className="flex flex-col sm:flex-row gap-4 mb-10"
            >
              <motion.a
                href={`https://wa.me/${contactData.whatsapp.replace(/\D/g, '')}?text=Olá! Gostaria de agendar uma consultoria.`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary flex items-center justify-center gap-2 text-decoration-none magnetic-btn"
                whileHover={{ scale: 1.04, y: -3 }}
                whileTap={{ scale: 0.97 }}
                transition={{ type: 'spring', stiffness: 400, damping: 20 }}
              >
                {heroData.ctaPrimary}
                <motion.span animate={{ x: [0, 4, 0] }} transition={{ repeat: Infinity, duration: 1.6, ease: 'easeInOut' }}>
                  <ArrowRight size={16} />
                </motion.span>
              </motion.a>

              <motion.button
                onClick={() => handleNavClick('#tools')}
                className="btn-secondary flex items-center justify-center gap-2"
                whileHover={{ scale: 1.04, y: -3 }}
                whileTap={{ scale: 0.97 }}
                transition={{ type: 'spring', stiffness: 400, damping: 20 }}
              >
                {heroData.ctaSecondary}
              </motion.button>
            </motion.div>

            {/* Social Proof */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 1.15 }}
              className="flex items-center gap-6 flex-wrap"
            >
              <div className="flex items-center gap-3">
                <div className="flex -space-x-2">
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className="w-9 h-9 rounded-full border-2 overflow-hidden"
                      style={{ borderColor: 'var(--bg-page)', background: 'var(--accent-bronze-200)' }}
                    >
                      <img src={`https://i.pravatar.cc/36?img=${i + 20}`} alt="Avatar" className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
                <div>
                  <div className="flex items-center gap-0.5 mb-0.5">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} size={12} fill="#B8860B" color="#B8860B" />
                    ))}
                  </div>
                  <p className="caption">+1000 {language === 'en' ? 'lives transformed' : 'vidas transformadas'}</p>
                </div>
              </div>

              <div className="hidden sm:block w-px h-10" style={{ background: 'var(--border-light)' }} />

              <div className="flex gap-6">
                {[
                  { val: '21+', label: language === 'en' ? 'Years of experience' : 'Anos de experiência' },
                  { val: '15+', label: language === 'en' ? 'Years of Divas Project' : 'Anos Projeto Divas' },
                ].map(({ val, label }, i) => (
                  <motion.div
                    key={i}
                    whileHover={{ y: -2 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                  >
                    <p className="heading-2" style={{ color: 'var(--accent-gold-400)' }}>{val}</p>
                    <p className="caption">{label}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>

      {/* ── Mobile Photo ─── */}
      <div className="lg:hidden absolute inset-0 z-0">
        <div className={`absolute inset-0 bg-gradient-to-b ${theme === 'dark' ? 'from-[#121212]/80 via-[#121212]/60 to-[#121212]' : 'from-[#FAF7F2]/80 via-[#FAF7F2]/60 to-[#FAF7F2]'} z-10`} />
        <img
          src={heroPhoto}
          alt="Lucimar Bizoni"
          className="w-full h-full object-cover opacity-60"
          style={{ objectPosition: 'center top' }}
        />
      </div>

      {/* ── Bottom Fade ─── */}
      <div className={`absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t ${theme === 'dark' ? 'from-[#121212]' : 'from-[#FAF7F2]'} to-transparent z-10`} />


    </section>
  );
};

export default HeroSection;
